import { useUiText } from "../../locale/UiLocale";
import type { ReactNode } from "react";
import { EmptyState } from "../EmptyState/EmptyState";
import { alignClass } from "./columnLayout";
import type { Column, DataTableEmpty } from "./DataTableTypes";
import { skeletonBarStyle } from "./skeleton";

const DEFAULT_EMPTY: DataTableEmpty = { icon: "inbox", title: "Nothing here yet", description: "There is no data to show yet." };

/** A single row spanning every column, for messages in place of the rows. */
function StateRow({ colSpan, children }: { colSpan: number; children: ReactNode }): React.JSX.Element {
  return (
    <tr className="nb-dt-state">
      <td colSpan={colSpan}>{children}</td>
    </tr>
  );
}

export function ErrorRow({ colSpan, error }: { colSpan: number; error: DataTableEmpty }): React.JSX.Element {
  return (
    <StateRow colSpan={colSpan}>
      <EmptyState icon={error.icon ?? "alertCircle"} title={error.title} description={error.description} action={error.action} />
    </StateRow>
  );
}

/** Shown when the table has no rows at all. */
export function EmptyRow({ colSpan, empty = DEFAULT_EMPTY }: { colSpan: number; empty?: DataTableEmpty }): React.JSX.Element {
  const t = useUiText();
  return (
    <StateRow colSpan={colSpan}>
      <EmptyState icon={empty.icon} title={t(empty.title)} description={typeof empty.description === "string" ? t(empty.description) : empty.description} action={empty.action} />
    </StateRow>
  );
}

/** Shown when the search and filters leave no rows. */
export function NoResultsRow({ colSpan, onClear }: { colSpan: number; onClear: () => void }): React.JSX.Element {
  const t = useUiText();
  return (
    <StateRow colSpan={colSpan}>
      <EmptyState
        icon="search"
        title={t("No results")}
        description={t("Nothing matches your search or filters.")}
        action={
          <button type="button" className="nb-btn nb-btn-secondary nb-btn-sm" onClick={onClear}>
            {t("Clear filters")}
          </button>
        }
      />
    </StateRow>
  );
}

/** Placeholder rows shaped like the columns while the data loads. */
export function SkeletonRows<R>({ columns, count }: { columns: Column<R>[]; count: number }): React.JSX.Element {
  return (
    <>
      {Array.from({ length: count }, (_, rowIndex) => (
        <tr key={`skel-${rowIndex}`} className="nb-dt-skel">
          {columns.map((col, columnIndex) => (
            <td key={col.key} className={alignClass(col)}>
              {col.type === "actions" ? null : <span className="nb-skel" style={skeletonBarStyle(col, rowIndex, columnIndex)} />}
            </td>
          ))}
        </tr>
      ))}
    </>
  );
}
